import { ConnectKitButton } from "connectkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { sepolia } from "viem/chains";
import { abi as erc20ABI, getAddress, getEIP712Domain, permitTypes, Token } from "./ERC20";

import {
    useAccount,
    useReadContract,
    useSignTypedData,
    useSimulateContract,
    useSwitchChain,
    useWaitForTransactionReceipt,
    useWriteContract,
} from "wagmi";
import { EntityDetails } from "./lib/types";
import useEcho from "./lib/useEcho";
import { plasmaSaleAbi } from "./PlasmaSaleABI";

type BasicPermit = {
    EntityID: Uint8Array;
    SaleUUID: Uint8Array;
    Wallet: string;
    ExpiresAt: string;
    Payload: Uint8Array;
};

type AllocationPermit = {
    Permit: BasicPermit;
    ReservedAmount: string;
    MaxAmount: string;
};

// TODO move to config
const chainId = sepolia.id;
const saleAddress = "0x1bDEC61D634dF04F54dbe592b851394FE0a8E377" as `0x${string}`;

const PlasmaSale = ({ selectedEntity }: { selectedEntity: EntityDetails }) => {
    const { client, generatePurchasePermit } = useEcho();
    const { address: userAddress, isConnecting, chainId: userChainId } = useAccount();
    const [permitSignature, setPermitSignature] = useState<string | null>(null);
    const [permitJSON, setPermitJSON] = useState<AllocationPermit | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [token, setToken] = useState<Token>(Token.USDC);
    const tokenSupportsPermits = token !== Token.USDT;

    // TODO move to state with input
    const tokenAmount = BigInt(10000);

    const { switchChain } = useSwitchChain();
    useEffect(() => {
        if (userChainId !== chainId) {
            switchChain({ chainId: chainId });
        }
    }, [userChainId, switchChain]);

    const {
        data: erc20Balance,
        error: erc20BalanceError,
        status: erc20BalanceStatus,
    } = useReadContract({
        chainId: sepolia.id,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "balanceOf",
        args: [userAddress!],
        query: {
            select: (data: any) => data as bigint,
            enabled: Boolean(userAddress),
        },
    });

    const purchase = useCallback(async () => {
        if (!client || !selectedEntity || !userAddress) {
            return;
        }

        try {
            const response = await generatePurchasePermit({
                entityUUID: selectedEntity.EntityUUID,
                entityType: selectedEntity.EntityType,
                walletAddress: userAddress,
            });
            if (response.Signature && response.Permit) {
                setPermitSignature(response.Signature);
                setPermitJSON(response.Permit as unknown as AllocationPermit);
                return;
            }
        } catch (error) {
            setErrorMessage("Failed to generate purchase permit: " + error);
            return;
        }

        setErrorMessage("Failed to generate purchase permit");
    }, [client, selectedEntity, userAddress, generatePurchasePermit]);

    if (errorMessage) {
        return (
            <div className="flex gap-4">
                <div className="text-red-700">{errorMessage}</div>
                <button
                    onClick={() => {
                        setErrorMessage(null);
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!userAddress) {
        return (
            <div>
                <ConnectKitButton />
                <p className="text-gray-200 text-lg font-bold">Please connect your wallet</p>
            </div>
        );
    }

    if (isConnecting) {
        return <div>Connecting...</div>;
    }

    if (!permitSignature || !permitJSON) {
        return (
            <div>
                <p>Connected wallet: {userAddress}</p>
                <p>Entity: {selectedEntity.Label}</p>
                <p className="text-gray-200 text-lg font-bold">State: Ready to Purchase</p>
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={() => {
                        purchase();
                    }}
                >
                    Start Purchase
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-gray-200">Your permit signature: {permitSignature}</p>
            <p className="text-gray-200">
                Your permit JSON:{" "}
                <span className="whitespace-pre-wrap break-words">{JSON.stringify(permitJSON, hexReplacer, 2)}</span>
            </p>
            <select
                id="token"
                value={token}
                onChange={(e) => {
                    setToken(e.target.value as unknown as Token);
                }}
                className="text-black"
            >
                {Object.values(Token).map((t) => (
                    <option key={t} value={t}>
                        {t}
                    </option>
                ))}
            </select>

            <div className="flex flex-col gap-1">
                <p>
                    Balance [{erc20BalanceStatus}]: {erc20Balance?.toString()}
                </p>
                {erc20BalanceError && <p className="text-red-700">{erc20BalanceError.message}</p>}
            </div>

            {tokenSupportsPermits ? (
                <CommitWithPermit
                    userAddress={userAddress}
                    token={token}
                    tokenAmount={tokenAmount}
                    permitJSON={permitJSON}
                    permitSignature={permitSignature}
                />
            ) : (
                <CommitWithApproval
                    userAddress={userAddress}
                    token={token}
                    tokenAmount={tokenAmount}
                    permitJSON={permitJSON}
                    permitSignature={permitSignature}
                />
            )}
            <button
                className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                onClick={() => {
                    setPermitSignature(null);
                    setPermitJSON(null);
                }}
            >
                Reset
            </button>
        </div>
    );
};

const CommitWithPermit = ({
    userAddress,
    token,
    tokenAmount,
    permitJSON,
    permitSignature,
}: {
    userAddress: `0x${string}`;
    token: Token;
    tokenAmount: bigint;
    permitJSON: AllocationPermit;
    permitSignature: string;
}) => {
    const erc20PermitDeadline = BigInt(10000000000000000);

    // fetch the token nonce to generate a permit for
    const {
        data: erc20Nonce,
        error: erc20NonceError,
        status: erc20NonceStatus,
    } = useReadContract({
        chainId: sepolia.id,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "nonces",
        args: [userAddress],
        query: {
            select: (data: any) => data as bigint,
        },
    });

    // prepare the EIP712 signing
    const {
        signTypedDataAsync,
        data: erc20PermitSignature,
        error: erc20PermitError,
        reset: resetErc20Permit,
    } = useSignTypedData();

    // generate the permit signature
    const generateERC20Permit = useCallback(async () => {
        try {
            await signTypedDataAsync({
                domain: getEIP712Domain(chainId, token),
                types: permitTypes,
                primaryType: "Permit",
                message: {
                    owner: userAddress,
                    spender: saleAddress,
                    value: tokenAmount,
                    nonce: erc20Nonce,
                    deadline: erc20PermitDeadline,
                },
            });
        } catch (error) {
            console.error(error);
        }
    }, [userAddress, signTypedDataAsync, erc20PermitDeadline, tokenAmount, erc20Nonce, token]);

    // reset the permit signature when the token changes
    useEffect(() => {
        resetErc20Permit();
    }, [token, resetErc20Permit]);

    // simulate the commit with the permit transaction
    const {
        data: commitData,
        error: commitSimulationError,
        status: commitSimulationStatus,
    } = useSimulateContract({
        address: saleAddress,
        abi: plasmaSaleAbi,
        functionName: "commitWithPermit",
        args: [
            getAddress(chainId, token),
            tokenAmount,
            erc20PermitDeadline,
            erc20PermitSignature!,
            {
                permit: {
                    entityID: arrayToHex(permitJSON.Permit.EntityID),
                    saleUUID: arrayToHex(permitJSON.Permit.SaleUUID),
                    wallet: permitJSON.Permit.Wallet as `0x${string}`,
                    expiresAt: BigInt(permitJSON.Permit.ExpiresAt),
                    payload: arrayToHex(permitJSON.Permit.Payload),
                },
                reservedAmount: BigInt(permitJSON.ReservedAmount),
                maxAmount: BigInt(permitJSON.MaxAmount),
            },
            base64ToHex(permitSignature),
        ] as const,
        query: {
            enabled: Boolean(permitJSON) && Boolean(permitSignature) && Boolean(erc20PermitSignature),
        },
    });

    // prepare the contract write
    const {
        writeContractAsync: writeCommit,
        data: commitTxHash,
        status: commitStatus,
        error: commitError,
    } = useWriteContract();

    const { status: commitReceiptStatus, data: commitReceipt } = useWaitForTransactionReceipt({
        hash: commitTxHash,
    });

    // send the commit transaction
    const commit = useCallback(async () => {
        if (!commitData?.request) {
            return;
        }

        try {
            await writeCommit(commitData?.request);
        } catch (error) {
            console.error(error);
        }
    }, [writeCommit, commitData]);

    return (
        <>
            <div className="flex flex-col gap-2">
                <button className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono" onClick={generateERC20Permit}>
                    Generate
                </button>
                <div className="flex flex-col gap-1">
                    <p className="text-gray-200">
                        ERC20 permit nonce [{erc20NonceStatus}]: {erc20Nonce?.toString()}
                        {erc20NonceError ? "Error: " + erc20NonceError.message : ""}
                    </p>
                    <p className="text-gray-200">ERC20 permit signature: {erc20PermitSignature} </p>
                    {erc20PermitError && <p className="text-red-700">Error: {erc20PermitError.message}</p>}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={commit}
                    disabled={!commitData}
                >
                    Send commitment
                </button>
                <div className="flex flex-col gap-1">
                    <p>Commit simulation status: {commitSimulationStatus}</p>
                    {commitSimulationError && (
                        <p className="text-red-700">
                            Error simulating commitment: {commitSimulationError?.message} {commitSimulationError?.name}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit write status: {commitStatus}</p>
                    {commitStatus === "success" && <p className="text-green-700">Commitment sent: {commitTxHash}</p>}
                    {commitError && <p className="text-red-700">{commitError.message}</p>}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit receipt status: {commitReceiptStatus}</p>
                    {commitReceipt && (
                        <p className="text-green-700">
                            {commitReceipt.status} {commitReceipt.transactionHash}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

const CommitWithApproval = ({
    userAddress,
    token,
    tokenAmount,
    permitJSON,
    permitSignature,
}: {
    userAddress: `0x${string}`;
    token: Token;
    tokenAmount: bigint;
    permitJSON: AllocationPermit;
    permitSignature: string;
}) => {
    // fetch the token allowance to check if the user has approved the sale to spend the token
    const {
        data: allowance,
        error: allowanceError,
        status: allowanceStatus,
        refetch: refetchAllowance,
    } = useReadContract({
        chainId: sepolia.id,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "allowance",
        args: [userAddress, saleAddress],
        query: {
            select: (data: any) => data as bigint,
        },
    });

    // check if the user has approved the sale to spend the token
    const approvalDone = useMemo(() => (allowance ?? 0n) >= tokenAmount, [allowance, tokenAmount]);

    // simulate the approval transaction
    const {
        data: approveData,
        error: approveSimulationError,
        status: approveSimulationStatus,
    } = useSimulateContract({
        chainId: chainId,
        address: getAddress(chainId, token),
        abi: erc20ABI,
        functionName: "approve",
        args: [saleAddress, tokenAmount],
        query: {
            enabled: !approvalDone,
        },
    });

    // prepare the contract write
    const {
        writeContractAsync: approve,
        data: approveTxHash,
        status: approveStatus,
        error: approveError,
    } = useWriteContract();

    const { status: approveReceiptStatus, data: approveReceipt } = useWaitForTransactionReceipt({
        hash: approveTxHash,
    });

    // send the approval transaction
    const approveERC20 = useCallback(async () => {
        if (!approveData?.request) {
            return;
        }
        try {
            await approve(approveData?.request);
        } catch (error) {
            console.error(error);
        }
    }, [approve, approveData]);

    // on success we want to refresh the allowance so the UI reflects the current chain state
    useEffect(() => {
        if (approveReceiptStatus === "success") {
            refetchAllowance();
        }
    }, [approveReceiptStatus, refetchAllowance]);

    // simulate the commit with approval
    const {
        data: commitWithApprovalData,
        error: commitWithApprovalSimulationError,
        status: commitWithApprovalSimulationStatus,
    } = useSimulateContract({
        address: saleAddress,
        abi: plasmaSaleAbi,
        functionName: "commitWithApproval",
        args: [
            getAddress(chainId, token),
            tokenAmount,
            {
                permit: {
                    entityID: arrayToHex(permitJSON.Permit.EntityID),
                    saleUUID: arrayToHex(permitJSON.Permit.SaleUUID),
                    wallet: permitJSON.Permit.Wallet as `0x${string}`,
                    expiresAt: BigInt(permitJSON.Permit.ExpiresAt),
                    payload: arrayToHex(permitJSON.Permit.Payload),
                },
                reservedAmount: BigInt(permitJSON.ReservedAmount),
                maxAmount: BigInt(permitJSON.MaxAmount),
            },
            base64ToHex(permitSignature),
        ] as const,
        query: {
            enabled: Boolean(permitJSON) && Boolean(permitSignature) && approvalDone,
        },
    });

    console.log(commitWithApprovalData, Boolean(permitJSON), Boolean(permitSignature), approvalDone);

    // prepare the contract write
    const {
        writeContractAsync: writeCommit,
        data: commitTxHash,
        status: commitStatus,
        error: commitError,
    } = useWriteContract();

    const { status: commitReceiptStatus, data: commitReceipt } = useWaitForTransactionReceipt({
        hash: commitTxHash,
    });

    // send the commit transaction
    const commit = useCallback(async () => {
        if (!commitWithApprovalData?.request) {
            return;
        }

        try {
            await writeCommit(commitWithApprovalData?.request);
        } catch (error) {
            console.error(error);
        }
    }, [writeCommit, commitWithApprovalData]);

    return (
        <>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                    <p>
                        Allowance [{allowanceStatus}]: {allowance?.toString()}
                    </p>
                    {allowanceError && <p className="text-red-700">{allowanceError.message}</p>}
                </div>
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={approveERC20}
                    disabled={!approveData?.request}
                >
                    Approve
                </button>
                <div className="flex flex-col gap-1">
                    <p>Approval simulation status: {approveSimulationStatus}</p>
                    {approveSimulationError && (
                        <p className="text-red-700">
                            Error simulating approval: {approveSimulationError?.message} {approveSimulationError?.name}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Approve write: {approveStatus}</p>
                    {approveStatus === "success" && <p className="text-green-700">Approval sent: {approveTxHash}</p>}
                    {approveError && <p className="text-red-700">{approveError.message}</p>}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Approve receipt status: {approveReceiptStatus}</p>
                    {approveReceipt && (
                        <p className="text-green-700">
                            {approveReceipt.status} {approveReceipt.transactionHash}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <button
                    className="bg-white text-gray-800 rounded-md px-4 py-2 font-mono"
                    onClick={commit}
                    disabled={!commitWithApprovalData?.request}
                >
                    Send commitment
                </button>
                <div className="flex flex-col gap-1">
                    <p>Commit simulation status: {commitWithApprovalSimulationStatus}</p>
                    {commitWithApprovalSimulationError && (
                        <p className="text-red-700">
                            Error simulating commitment: {commitWithApprovalSimulationError?.message}{" "}
                            {commitWithApprovalSimulationError?.name}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit write status: {commitStatus}</p>
                    {commitStatus === "success" && <p className="text-green-700">Commitment sent: {commitTxHash}</p>}
                    {commitError && <p className="text-red-700">{commitError.message}</p>}
                </div>
                <div className="flex flex-col gap-1">
                    <p>Commit receipt status: {commitReceiptStatus}</p>
                    {commitReceipt && (
                        <p className="text-green-700">
                            {commitReceipt.status} {commitReceipt.transactionHash}
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

function isNumberArray(value: unknown): value is number[] {
    return (
        Array.isArray(value) && value.every((v) => typeof v === "number" && Number.isInteger(v) && v >= 0 && v <= 255)
    );
}

function hexReplacer(_key: string, value: unknown): unknown {
    if (value instanceof Uint8Array || isNumberArray(value)) {
        return arrayToHex(Array.from(value));
    }
    return value;
}

function arrayToHex(arr: Uint8Array | number[] | undefined | null): `0x${string}` {
    if (!arr) {
        return "0x";
    }

    return `0x${Array.from(arr)
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("")}`;
}

function base64ToHex(base64: string | null): `0x${string}` {
    if (!base64) {
        return "0x";
    }
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return arrayToHex(bytes);
}

export default PlasmaSale;
